import React from 'react';

import { useId } from '@coursera/cds-common';
import type {
  BaseComponentProps,
  ForwardRefComponent,
} from '@coursera/cds-common';

import { BaseDialog } from '@core/dialogs/common/BaseDialog';
import type { CommonDialogProps } from '@core/dialogs/common/types';
import { DialogContextProvider } from '@core/dialogs/Dialog/DialogContextProvider';

import { Close } from './Close';
import type {
  DialogAlignment,
  DialogColor,
  DialogVariant,
  DialogWidth,
} from './styles';
import {
  dialogClasses,
  getContentWrapperCss,
  getDialogCss,
  getSlotCss,
  isCloseAlwaysAbsolute,
  isCloseAbsoluteOnMobile,
} from './styles';

type DialogType = ForwardRefComponent<HTMLDivElement, Props>;

/**
 * Props for the Dialog component.
 */
export type Props = CommonDialogProps & {
  /**
   * Use the subcomponents to compose a Dialog:
   *
   * ```
   * <Dialog>
   *   <Dialog.HeadingGroup/>
   *   <Dialog.Content />
   *   <Dialog.Actions />
   * </Dialog>
   * ```
   *
   * @see Dialog.HeadingGroup
   * @see Dialog.Content
   * @see Dialog.Actions
   */
  children: React.ReactNode | React.ReactNode[];

  /**
   * Background color to apply to the header and the heading group.
   *
   * @default white
   */
  color?: DialogColor;

  /**
   * Controls the variant of the dialog.
   * Standard dialog will allow the user to exit using escape key, background overlay or close button.
   * Transactional dialog requires user to take an action and does not provide an option to exit without choosing to confirm or reject.
   *
   * @default standard
   */
  variant?: DialogVariant;

  /**
   * Props to pass to the slot container element.
   * Use this to customize the slot's style, className, css, etc.
   */
  slotProps?: Omit<BaseComponentProps<'div'>, 'children'>;

  /**
   * Content to render in the slot area of the dialog.
   * Commonly used for images, illustrations, or other media.
   * When undefined, no slot is rendered.
   */
  slotContent?: React.ReactNode;

  /**
   * Controls the placement of the slot content relative to the dialog body.
   * - horizontalStart: slot is on the left side of the content
   * - horizontalEnd: slot is on the right side of the content
   * - verticalTop: slot is stacked above the content
   *
   * Only applicable when `slotContent` is provided.
   */
  slotOrientation?: 'horizontalStart' | 'horizontalEnd' | 'verticalTop';

  /**
   * Sets the desired width behavior of the dialog.
   *
   * Note: While all widths are technically supported with horizontal slots,
   * `width="small"` with `slotOrientation="horizontalStart"` or `"horizontalEnd"`
   * may result in cramped layouts and is not recommended.
   *
   * @default medium
   */
  width?: DialogWidth;

  /**
   * Controls the alignment of content within the dialog.
   * - start: left-aligned heading text and left-aligned action buttons (default)
   * - center: centered heading text and centered action buttons
   *
   * Note: While `alignment="center"` is technically supported with horizontal slots,
   * it is not recommended as the side slot creates visual asymmetry that conflicts
   * with centered content. Center alignment works best with no slot or `verticalTop`.
   *
   * @default start
   */
  alignment?: DialogAlignment;

  /**
   * Controls which elements stick to the top/bottom when scrolling.
   * - headingGroup: heading group stays fixed at top when content scrolls
   * - actions: action buttons stay fixed at bottom when content scrolls
   * - none: no sticky behavior (default)
   *
   * Note: `sticky="headingGroup"` is automatically disabled when
   * `slotOrientation="verticalTop"` because the slot occupies the top position
   * and a sticky heading would overlap or conflict with the slot content.
   *
   * @default none
   */
  sticky?: 'headingGroup' | 'actions' | 'none';
};

const getHeadingGroupId = (node: React.ReactNode, baseId?: string) => {
  const configuredId = React.isValidElement(node) ? node.props.id : undefined;
  return configuredId || `${baseId}-headingGroup`;
};

const Dialog: DialogType = React.forwardRef(function Dialog(
  props: Props,
  ref: React.Ref<HTMLDivElement>
) {
  const {
    children,
    alignment = 'start',
    color = 'white',
    sticky = 'none',
    variant = 'standard',
    width = 'medium',
    slotContent,
    slotOrientation: slotOrientationProp,
    slotProps,
    id: baseId,
    onClose,
    'aria-labelledby': labelledBy,
    css: consumerCss,
    ...baseDialogProps
  } = props;

  const id = useId(baseId);

  const [headingGroup] = React.Children.toArray(children);
  const headingGroupId = getHeadingGroupId(headingGroup, id);

  const isTransactionalDialog = props.variant === 'transactional';
  const handleOnClose = isTransactionalDialog ? undefined : onClose;

  const hasSlot = slotContent !== undefined;
  // Default to horizontalStart when slotContent is provided but no orientation specified
  const slotOrientation = hasSlot
    ? slotOrientationProp ?? 'horizontalStart'
    : undefined;

  // Determine if close button should be absolutely positioned
  // Always absolute: verticalTop, horizontalEnd
  // Absolute on mobile only: horizontalStart
  const closeAlwaysAbsolute = isCloseAlwaysAbsolute(slotOrientation);
  const closeAbsoluteOnMobile = isCloseAbsoluteOnMobile(slotOrientation);

  const closeButton = (
    <Close showCloseAction={!isTransactionalDialog} onClose={handleOnClose} />
  );

  return (
    <BaseDialog
      ref={ref}
      enableScrollContainerTestId
      aria-labelledby={labelledBy || headingGroupId}
      classes={{ dialog: dialogClasses.dialog }}
      css={[getDialogCss({ width, hasSlot, slotOrientation }), consumerCss]}
      id={id}
      transition="fade-in"
      onClose={handleOnClose}
      {...baseDialogProps}
    >
      <DialogContextProvider
        alignment={alignment}
        closeAbsoluteOnMobile={closeAbsoluteOnMobile}
        color={color}
        headingGroupId={headingGroupId}
        slotOrientation={slotOrientation}
        sticky={sticky}
        variant={variant}
        width={width}
      >
        {hasSlot && slotOrientation ? (
          <>
            {closeAlwaysAbsolute && closeButton}
            <div
              {...slotProps}
              className={dialogClasses.slot}
              css={[getSlotCss(slotOrientation, color), slotProps?.css]}
            >
              {slotContent}
            </div>
            <div
              className={dialogClasses.contentWrapper}
              css={getContentWrapperCss({
                isCloseAbsolute: closeAlwaysAbsolute,
                color,
                slotOrientation,
                hasSlot,
              })}
            >
              {!closeAlwaysAbsolute && closeButton}
              {children}
            </div>
          </>
        ) : (
          <>
            {closeButton}
            {children}
          </>
        )}
      </DialogContextProvider>
    </BaseDialog>
  );
});

export default Dialog;
